<template>
    <div class="device_detail_main_card" >
        <div class="device_detail_main_card_title">
            <div>{{ $t('subscription') }}</div>
        </div>
        <div class="device_detail_main_card_less" v-if="subscription">
            <ReoDescription
                :title="$t('dashboard.current_plan')"
                :content="planInfo?.title"
            />
            <div class="description_renew">
                <ReoDescription
                    :title="$t('dashboard.device_detail.valid_until')"
                    :content="subscriptionActive?formatTime( subscription.endingAt, $t, '12' ):$t('dashboard.device_detail.main_card.expired')"
                />
            </div>
            <div class="device_detail_main_card_item" v-if="shouldShowStorageDurationEdit">
                <p class="device_detail_main_card_dark_text">
                    {{ $t('dashboard.device_detail.storage_history.title') }}
                    <Tip>{{ $t('dashboard.device_detail.storage_history_tip') }}</Tip>
                </p>
                <p v-if="!showItems.showDayEdit">
                    {{ storageDay.toString() +$t('dashboard.device_detail.storage_history.days') }}
                    <button class="device_detail_main_card_edit" @click="showItems.showDayEdit=true" v-if="subscriptionActive">
                        <img :src="EditIcon" alt="edit icon" />
                    </button>
                </p>
                <p v-else class="device_detail_days_edit">
                    <span class="device_detail_days_edit_select">
                        <reo-select
                            :widthOut='selectorStyle.widthOut'
                            :heightOut='selectorStyle.heightOut'
                            :widthIn='selectorStyle.widthIn'
                            :heightIn='selectorStyle.heightIn'
                            :selectMarginLeft='selectorStyle.selectMarginLeft'
                            :triangleRight='selectorStyle.triangleRight'
                            :maxHeight='selectorStyle.maxHeight'
                            :fontSizeIn='selectorStyle.fontSizeIn'
                            :fontSizeOut='selectorStyle.fontSizeOut'
                            :options="dayOptions"
                            :defaultSelected="storageDay"
                            :key='isMobile'
                            @input="select=>inputStorageDay=select"
                        >
                        </reo-select>
                        {{ $t('dashboard.device_detail.storage_history.days') }}
                    </span>
                    <span class="device_detail_days_edit_buttons">
                        <ReoCommonButton
                            :label="$t('dashboard.device_detail.storage_history.save')"
                            size="small"
                            @click="saveStorageDay"
                        />
                        <ReoCommonButton
                            :label="$t('dashboard.device_detail.storage_history.cancel')"
                            size="small"
                            colorType="light"
                            @click="showItems.showDayEdit=false;inputStorageDay=storageDay"
                        />
                    </span>
                </p>
            </div>
            <reo-common-button
                :label="$t('my_sim.manage_subscription')"
                color-type="light"
                class="more_button"
                size="middle"
                :custom-width="isMobile? '100%':undefined"
                to="/user/dashboard"
            />
        </div>
        <div class="device_detail_main_card_content device_detail_main_card_no_plan" v-else>
            <p>{{ $t('dashboard.device_detail.no_plan') }}</p>
            <ReoCommonButton :label="$t('dashboard.device_detail.purchase')" size="middle" @click="navigateToPurchase"/>
        </div>
    </div>
</template>

<script lang="ts">
import { timeResolve } from '@/common/js/date';
import { calcFinalDataStr } from '@/common/js/utils';
import { ESeriesCategory, IPlan, IReoSelectOption, ISelectorStyle, ISubDevice, ISubscription } from '@reolink-cloud/interface';
import { config } from 'CONFIG';
import Vue, { PropType } from 'vue';
import ReoDescription from './components/ReoDescription.vue';
import ReoSelect from '@/basic/ReoSelect.vue';
import Tip from '@/basic/Tip.vue';
import EditIcon from '@/common/images/common/combo/co_edit.svg';
import { setVideoRetention } from '@/api/cloud';
import { unfoldError } from '@/common/js/notice';
import { formatTime } from '@/utils/time';
import reolinkGa from '@/common/js/gtagPageview';

export default Vue.extend({
    components: {
        ReoDescription,
        ReoSelect,
        Tip
    },
    props: {
        subscription: Object as PropType<ISubscription | undefined>,
        subDevice: Object as PropType<ISubDevice | undefined>,
        planInfo: Object as PropType<IPlan | undefined>,
        from: String as PropType<'my-devices' | 'cloud-dashboard'>,
    },
    data() {

        return {
            showItems: {
                showDialogs: {
                    addOn: false,
                    autoRenew: false,
                    disableAutoRenew: false,
                    signPaypal: false
                },
                mainCardShowMore: false,
                moreMenuShow: false,
                cancelMenuShow: false,
                showDayEdit: false,
            },
            config,
            inputStorageDay: 0,
            EditIcon
        };

    },
    computed: {
        subscriptionActive(): boolean {

            return this.subscription?.status === 'active';

        },
        isMobile(): boolean {

            return this.$store.getters.isMobile;

        },
        planTitle(): string {

            if ( this.planInfo ) {

                return this.$options.filters?.planTitle( this.planInfo ) ?? '';

            }
            return '';

        },
        dayOptions(): IReoSelectOption[] {

            return  [1, 3, 7, 14, 21, 30, 60]
                .filter( num => {

                    return num <= ( Reflect.get( this.planInfo?.style ?? {}, 'cloud_storage.video_lifetime' ) ?? 60 );

                })
                .map( num => {

                    return { label: num.toString(), value: num, id: num };

                });

        },
        storageDay(): number {

            return this.subDevice?.videoRetentionDays ?? 0;

        },
        gaSuffer(): string {

            switch ( this.from ) {

                case 'my-devices':
                    return 'Devices';
                case 'cloud-dashboard':
                    return 'Dashboard';
                default:
                    return '';

            }

        },
        /**
         * 判断是否应显示存储时长编辑功能
         * 带图推送套餐不支持修改存储时长，需隐藏该字段
         * 
         * 套餐类型判断逻辑:
         * - ESeriesCategory.CLOUD_RICH_NOTIFICATION_PLAN: 带图推送套餐，不支持存储时长编辑
         * - 其他类型：云存储套餐，支持存储时长编辑
         */
        shouldShowStorageDurationEdit(): boolean {

            return this.planInfo?.series?.category !== ESeriesCategory.CLOUD_RICH_NOTIFICATION_PLAN;

        },
        selectorStyle (): ISelectorStyle {

            return !this.isMobile ? {
                widthOut: 1.16,
                heightOut: 0.32,
                widthIn: 1.16,
                heightIn: 0.32,
                selectMarginLeft: 0.16,
                triangleRight: 0.16,
                maxHeight: 3.08,
                fontSizeIn: 0.16,
                fontSizeOut: 0.16
            } : {
                widthOut: -1,
                heightOut: 0.32,
                widthIn: -1,
                heightIn: 0.4,
                selectMarginLeft: 0.12,
                triangleRight: 0.12,
                maxHeight: 4.75,
                fontSizeIn: 0.17,
                fontSizeOut: 0.13
            };

        }

    },
    methods: {
        formatTime,
        calcFinalDataStr,
        async saveStorageDay() {

            if ( this.inputStorageDay !== 0 ) {

                try {

                    await setVideoRetention( [{ uid: this.subDevice!.uid, retentionDays: this.inputStorageDay }], this.subscription?.id ?? '' );
                    this.$emit( 'refresh', this.inputStorageDay );
                    this.showItems.showDayEdit = false;

                } catch ( e ) {

                    console.error( e );
                    unfoldError( 'dashboard.device_detail.main_card.change_storage_day_error',  3000 );

                }

            }

        },
        async navigateToPurchase() {

            reolinkGa.sendSimpleEvent( `${this.gaSuffer}-Cloud-Purchase a Plan` );
            await this.$router.push( '/user/subscribe-plan/cloud/' );

        }
    }
});

</script>

<style lang="less" scoped>
.device_detail_main_card {
    .reo-card();
    font-family: @font_02;
    line-height: normal;
    letter-spacing: 0em;

    .device_detail_main_card_title {
        display: flex;
        justify-content: space-between;
        padding: 16px 20px;
        font-size: 20px;
        font-weight: 600;
        border-bottom: 1px solid @line;

        @media @mobile {
            font-size: 22px;
            align-items: center
        }

        .link{
            font-size: 20px;
        }

    }

    .device_detail_main_card_more_menu {
        border: none;
        background: none;
        cursor: pointer;
        padding: 0;
        position: relative;
        padding-top: 24px;
        @mask_z_index: 100;

        img {
            width: 24px;
            vertical-align: bottom;

            @media @mobile {
                width: 18px;
            }
        }
        .more-menu-mask{
            position: fixed;
            width: 100vw;
            height: 100vh;
            background-color: @black6;
            top: 0;
            left: 0;
            z-index: @mask_z_index;
        }

        .device_detail_main_card_more_menu_items {
            position: absolute;
            top: 70%;
            right: 15px;
            z-index: 10;
            background-color: @bg_02;
            border: 1px solid @line;
            border-radius: 8px;
            box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.23);
            z-index: @mask_z_index + 1;

            @media @mobile {
                position: fixed;
                bottom: 0;
                left: 0;
                top: unset;
                right: unset;
                width: 100%;
                box-sizing: border-box;
            }
            li:first-of-type{
                border-top-left-radius: 8px;
                border-top-right-radius: 8px;
                .mobile({
                    padding-top: 26px;
                })
            }
            li:last-of-type{
                border-bottom-left-radius: 8px;
                border-bottom-right-radius: 8px;
                .mobile({
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                    padding-bottom: 28px;
                })
            }

            li {
                position: relative;
                font-family: @font_02;
                font-size: 14px;
                font-weight: 500;
                line-height: normal;
                letter-spacing: 0em;
                color: @font_primary;
                width: 280px;
                display: flex;
                padding: 8px 6px;
                flex-direction: column;
                align-items: center;
                gap: 4px;
                line-break: anywhere;
                .mobile({
                    width: 100%;
                    box-sizing: border-box;
                    padding: 16px;
                    border-top: 1px solid @line;
                });

                .device_detail_main_card_more_menu_items {
                    left: 100%;
                    top: 0;
                    right: unset;

                    @media @mobile {
                        left: 0;
                        top: unset;
                        z-index: @mask_z_index + 2;
                    }
                }

                &:hover {
                    background-color: @brand_light;
                    color: @white;
                }

                &.active {
                    background-color: @brand_light;
                    color: @white;
                }

                .danger_tip {
                    text-align: center;
                    font-size: 12px;
                    font-weight: 500;
                    color: @error;
                }
            }
        }

    }

    .device_detail_main_card_dark_text {
        white-space: pre;
        font-size: 18px;
        font-weight: 600;
    }

    .device_detail_main_card_less {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 32px 20px;
        align-items: flex-start;

        .device_detail_main_card_times {
            display: flex;
            flex-direction: column;
            gap: 6px;
            margin-top: 12px;
            margin-bottom: 16px;

            @media @mobile {
                margin-bottom: 12px;
            }

            &>p {
                display: flex;
                align-items: center;

                img {
                    width: 16px;
                    margin-right: 8px;
                }
            }
        }
    }

    .device_detail_main_card_dark_text {
        white-space: normal;
    }

    .device_detail_main_card_item {
        display: flex;
        flex-direction: column;
        gap: 4px;
        .device_detail_main_card_edit{
            border: none;
            background-color: transparent;
            padding: 0;
            cursor: pointer;
            img{
                width: 18px;
                vertical-align: bottom;
            }
        }

        .device_detail_days_edit{
            display: flex;
            align-items: flex-end;
            .device_detail_days_edit_select{
                display: inline-flex;
                align-items: center ;
                gap: 4px;
                @media @mobile{
                    width: 100px;
                }
            }
            .device_detail_days_edit_buttons{
                margin-left: 21px;
                display: inline-flex;
                gap: 12px;
            }
        }
    }

    .device_detail_main_card_add_on {
        .device_detail_main_card_dark_text {
            display: flex;
            justify-content: space-between;
            align-items: center
        }

        .add_on_dialog_content {
            display: flex;
            flex-direction: column;
            text-align: left;
            padding-bottom: 24px;
            .title{
                margin-top: 5px;
                font-weight: 700;
            }

            &_last {
                padding-top: 24px;
                border-bottom: none;
                p{
                    font-size: 15px;
                    font-weight: 500;
                }
            }

            .add_on_dialog_content_secondary{
                color: @font_secondary
            }

        }

        .add_on_info_card{
            background-color: @bg_01;
            border-radius: 12px;
            padding: 16px;
            gap: 12px;
            &:not(:first-of-type){
                margin-top: 12px;
            }
            .number{
                font-size: 14px;
                font-weight: 500;
                color: @white;
                width: 18px;
                height: 18px;
                background-color: @b_4;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 100%;
                margin-right: 8px;
            }
            p{
                font-size: 16px;
                font-weight: 500;
            }
        }

        ol {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-top: 8px;

            li {

                font-family: @font_02;
                font-size: 14px;
                font-weight: 500;
                line-height: normal;
                letter-spacing: 0em;
                color: @font_secondary;
                display: flex;
                gap: 8px;
                align-items: center;

                .order {
                    border-radius: 100%;
                    background-color: @b_4;
                    font-size: 14px;
                    font-weight: 500;
                    color: white;
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

            }
        }
    }

    .device_detail_main_card_no_plan {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 32px 20px;
        align-items: flex-start;
        p{
            font-size: 18px;
            font-weight: 600;
        }

        .ui-link {
            .text {
                font-size: 14px;
            }
        }
    }

    .device_detail_detail_btn {
        border: none;
        background-color: transparent;
        font-family: @font_02;
        font-size: 14px;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0em;
        color: @brand_light;
        cursor: pointer;
        padding: 0;

        &:hover {
            color: @brand_light_hover;
        }
    }

    .device_detail_main_card_bottom {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: @bg_01;
        padding: 12px;
        margin-top: 8px;

        .device_detail_main_card_bottom_left {
            display: flex;
            flex-direction: column;
            gap: 4px;
            font-family: @font_02;
            line-height: normal;
            letter-spacing: 0em;

            .device_detail_main_card_bottom_left_title {
                font-size: 16px;
                font-weight: 500;
            }

            .device_detail_main_card_bottom_left_content {
                font-size: 16px;
                font-weight: 500;
                color: @font_secondary;
            }
        }
    }

}
</style>
<style>
body:has(.more-menu-mask){
    overflow: hidden;
}
</style>
